import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Drawer, message, Modal, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
    GET_SCAN_OTHER_DRIVER_PARCEL_DETAIL_FOR_CURRENT_DRIVER,
    ScanOtherDriverParcelDetailResponse,
    ScanOtherDriverParcelDetailType
} from "../graphql/queries/getScanOtherDriverParcelDetailForCurrentDriver";
import { formatTimestampToDateTime } from "../../../../utils/dateTimeUtils";
import { BATCH_HANDLE_DRIVER_MIS_SCAN_NOT_RETURNED } from "../graphql/mutations/batchHandleDriverMisScanNotReturned";
import { BATCH_HANDLE_DRIVER_MIS_SCAN_CONFIRM_RECEIVE } from "../graphql/mutations/batchHandleDriverMisScanConfirmReceive";
import CentralParcelDetailDrawer from "../../../central/parcels/components/CentralParcelDetailDrawer";
import { getUserInfo } from "../../../../utils/auth";

interface ScanOtherDriverParcelDrawerProps {
    visible: boolean;
    title: string;
    driverAccountId?: number;
    onClose: () => void;
    onDataRefresh: () => void;
}

interface RouteSummaryRow {
    id: string;
    routeName: string;
    count: number;
    assignedDriverName: string;
    assignedDriverPhone: string;
}

interface MisScanTableRow {
    key: React.Key;
    isRouteRow: boolean;
    routeName: string;
    count?: number;
    trackingNumber?: string;
    assignedDriverName: string;
    assignedDriverPhone: string;
    isDriverNoReturnMarked?: boolean;
    scanTime?: number | string | null;
    orderId?: number | null;
    deliveryTaskOrderId?: number | null;
    children?: MisScanTableRow[];
}

interface MisScanParcelInput {
    deliveryTaskOrderId?: number;
    trackingNumber?: string;
}

const ScanOtherDriverParcelDrawer: React.FC<ScanOtherDriverParcelDrawerProps> = ({
    visible,
    title,
    driverAccountId,
    onClose,
    onDataRefresh
}) => {
    const { t } = useTranslation();
    const formatMisScanTime = (timestamp?: number | string | null) => {
        if (!timestamp) return "-";
        return formatTimestampToDateTime(timestamp).replace(/\//g, "-");
    };
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [confirmReceiveModalOpen, setConfirmReceiveModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [parcelDrawerVisible, setParcelDrawerVisible] = useState(false);
    const { data, loading, refetch } = useQuery<ScanOtherDriverParcelDetailResponse>(
        GET_SCAN_OTHER_DRIVER_PARCEL_DETAIL_FOR_CURRENT_DRIVER,
        {
            variables: { driverAccountId: driverAccountId || 0 },
            skip: !visible || !driverAccountId
        }
    );

    const detailList = data?.getScanOtherDriverParcelDetailForCurrentDriver || [];

    const groupedByRoute = useMemo(() => {
        return detailList.reduce((acc, item) => {
            const routeKey = item.routeName || "-";
            const bucket = acc.get(routeKey) || [];
            bucket.push(item);
            acc.set(routeKey, bucket);
            return acc;
        }, new Map<string, ScanOtherDriverParcelDetailType[]>());
    }, [detailList]);

    const routeRows: RouteSummaryRow[] = useMemo(() => {
        return Array.from(groupedByRoute.entries()).map(([routeName, items], idx) => {
            const first = items[0];
            return {
                id: `${routeName}-${idx}`,
                routeName,
                count: items.length,
                assignedDriverName: first?.assignedDriverName || "",
                assignedDriverPhone: first?.assignedDriverPhone || ""
            };
        });
    }, [groupedByRoute]);

    const [batchHandleNotReturned, { loading: notReturnedLoading }] = useMutation(
        BATCH_HANDLE_DRIVER_MIS_SCAN_NOT_RETURNED
    );
    const [batchHandleConfirmReceive, { loading: confirmReceiveLoading }] = useMutation(
        BATCH_HANDLE_DRIVER_MIS_SCAN_CONFIRM_RECEIVE
    );

    useEffect(() => {
        if (visible) {
            setExpandedRowKeys([]);
            setSelectedRowKeys([]);
            setConfirmReceiveModalOpen(false);
            setSelectedOrderId(null);
            setParcelDrawerVisible(false);
        }
    }, [visible]);

    const tableData: MisScanTableRow[] = useMemo(() => {
        return routeRows.map((routeRow) => {
            const routeDetails = groupedByRoute.get(routeRow.routeName) || [];
            const children: MisScanTableRow[] = routeDetails.map((detail, idx) => ({
                key: detail.deliveryTaskOrderId != null
                    ? `order-${detail.deliveryTaskOrderId}`
                    : `order-${routeRow.id}-${detail.trackingNumber || "unknown"}-${idx}`,
                isRouteRow: false,
                routeName: routeRow.routeName,
                trackingNumber: detail.trackingNumber,
                assignedDriverName: detail.assignedDriverName || "",
                assignedDriverPhone: detail.assignedDriverPhone || "",
                isDriverNoReturnMarked: detail.isDriverNoReturnMarked,
                scanTime: detail.scanTime,
                orderId: detail.orderId,
                deliveryTaskOrderId: detail.deliveryTaskOrderId
            }));
            return {
                key: routeRow.id,
                isRouteRow: true,
                routeName: routeRow.routeName,
                count: routeRow.count,
                assignedDriverName: routeRow.assignedDriverName,
                assignedDriverPhone: routeRow.assignedDriverPhone,
                children
            };
        });
    }, [routeRows, groupedByRoute]);

    const keyToParcelInputMap = useMemo(() => {
        const map = new Map<React.Key, MisScanParcelInput>();
        tableData.forEach(route => {
            route.children?.forEach(child => {
                if (child.deliveryTaskOrderId != null || child.trackingNumber) {
                    map.set(child.key, {
                        deliveryTaskOrderId: child.deliveryTaskOrderId ?? undefined,
                        trackingNumber: child.trackingNumber || undefined
                    });
                }
            });
        });
        return map;
    }, [tableData]);

    const disabledRouteKeySet = useMemo(() => {
        const set = new Set<React.Key>();
        tableData.forEach(route => {
            const hasSelectableChild = (route.children || []).some(
                child => child.deliveryTaskOrderId != null || !!child.trackingNumber
            );
            if (!hasSelectableChild) {
                set.add(route.key);
            }
        });
        return set;
    }, [tableData]);

    const routeSelectableChildKeyMap = useMemo(() => {
        const map = new Map<React.Key, React.Key[]>();
        tableData.forEach(route => {
            const keys = (route.children || [])
                .filter(child => child.deliveryTaskOrderId != null || !!child.trackingNumber)
                .map(child => child.key);
            map.set(route.key, keys);
        });
        return map;
    }, [tableData]);

    const selectedKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

    const isRouteChecked = (routeKey: React.Key) => {
        const childKeys = routeSelectableChildKeyMap.get(routeKey) || [];
        if (childKeys.length === 0) return false;
        return childKeys.every(k => selectedKeySet.has(k));
    };

    const isRouteIndeterminate = (routeKey: React.Key) => {
        const childKeys = routeSelectableChildKeyMap.get(routeKey) || [];
        if (childKeys.length === 0) return false;
        const checkedCount = childKeys.filter(k => selectedKeySet.has(k)).length;
        return checkedCount > 0 && checkedCount < childKeys.length;
    };

    const updateSelectionByRecord = (record: MisScanTableRow, checked: boolean) => {
        const next = new Set(selectedRowKeys);
        if (record.isRouteRow) {
            const childKeys = routeSelectableChildKeyMap.get(record.key) || [];
            if (checked) {
                childKeys.forEach(k => next.add(k));
                next.add(record.key);
            } else {
                childKeys.forEach(k => next.delete(k));
                next.delete(record.key);
            }
        } else {
            if (record.deliveryTaskOrderId == null && !record.trackingNumber) return;
            if (checked) {
                next.add(record.key);
            } else {
                next.delete(record.key);
            }
            const parent = tableData.find(route => (route.children || []).some(child => child.key === record.key));
            if (parent) {
                const parentChildKeys = routeSelectableChildKeyMap.get(parent.key) || [];
                const allChecked = parentChildKeys.length > 0 && parentChildKeys.every(k => next.has(k));
                if (allChecked) {
                    next.add(parent.key);
                } else {
                    next.delete(parent.key);
                }
            }
        }
        setSelectedRowKeys(Array.from(next));
    };

    const getSelectedParcels = (): MisScanParcelInput[] => {
        return Array.from(
            new Set(
                selectedRowKeys
                    .map(key => keyToParcelInputMap.get(key))
                    .filter((item): item is MisScanParcelInput => item != null)
                    .map(item => JSON.stringify(item))
            )
        ).map((item) => JSON.parse(item) as MisScanParcelInput);
    };

    const handleBatchAction = async (action: "notReturned") => {
        const parcels = getSelectedParcels();
        if (parcels.length === 0) {
            message.warning(t("wms.review.scanOtherDrawer.messages.selectParcelFirst"));
            return;
        }
        try {
            const res = await batchHandleNotReturned({ variables: { parcels } });
            if (res.data?.batchHandleDriverMisScanNotReturned) {
                message.success(t("wms.review.scanOtherDrawer.messages.handleSuccess"));
                setSelectedRowKeys([]);
                onDataRefresh();
                await refetch();
            }
        } catch (e: any) {
            message.error(e?.message || t("wms.review.scanOtherDrawer.messages.handleFailed"));
        }
    };

    const openConfirmReceiveModal = () => {
        const parcels = getSelectedParcels();
        if (parcels.length === 0) {
            message.warning(t("wms.review.scanOtherDrawer.messages.selectParcelFirst"));
            return;
        }
        setConfirmReceiveModalOpen(true);
    };

    const executeConfirmReceive = async () => {
        const parcels = getSelectedParcels();
        if (parcels.length === 0) {
            setConfirmReceiveModalOpen(false);
            return;
        }
        try {
            const res = await batchHandleConfirmReceive({ variables: { parcels } });
            if (res.data?.batchHandleDriverMisScanConfirmReceive) {
                message.success(t("wms.review.scanOtherDrawer.messages.handleSuccess"));
                setConfirmReceiveModalOpen(false);
                setSelectedRowKeys([]);
                onDataRefresh();
                await refetch();
            }
        } catch (e: any) {
            message.error(e?.message || t("wms.review.scanOtherDrawer.messages.handleFailed"));
        }
    };

    const columns: ColumnsType<MisScanTableRow> = [
        {
            title: "",
            dataIndex: "expandControl",
            width: 40,
            render: (_: unknown, record) => {
                if (!record.isRouteRow || (record.children?.length || 0) === 0) {
                    return null;
                }
                const expanded = expandedRowKeys.includes(record.key);
                return (
                    <span
                        style={{ cursor: "pointer", userSelect: "none", display: "inline-block", width: 16, textAlign: "center" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRowKeys(prev => {
                                if (expanded) return prev.filter(k => k !== record.key);
                                return [...prev, record.key];
                            });
                        }}
                    >
                        {expanded ? "−" : "+"}
                    </span>
                );
            }
        },
        {
            title: "",
            dataIndex: "selectControl",
            width: 48,
            render: (_: unknown, record) => {
                const disabled = record.isRouteRow
                    ? disabledRouteKeySet.has(record.key)
                    : (record.deliveryTaskOrderId == null && !record.trackingNumber);
                const checked = record.isRouteRow
                    ? isRouteChecked(record.key)
                    : selectedKeySet.has(record.key);
                const indeterminate = record.isRouteRow ? isRouteIndeterminate(record.key) : false;
                return (
                    <Checkbox
                        disabled={disabled}
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={(e) => updateSelectionByRecord(record, e.target.checked)}
                    />
                );
            }
        },
        {
            title: t("wms.review.scanOtherDrawer.columns.area"),
            dataIndex: "routeName",
            width: 220,
            align: "left",
            render: (_: string, record) => (
                record.isRouteRow
                    ? <span>{record.routeName || "-"}</span>
                    : <span
                        style={{
                            color: "#9254de",
                            whiteSpace: "nowrap",
                            cursor: record.orderId != null ? "pointer" : "default",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            fontVariantNumeric: "tabular-nums"
                        }}
                        onClick={() => {
                            if (record.orderId == null) return;
                            setSelectedOrderId(String(record.orderId));
                            setParcelDrawerVisible(true);
                        }}
                    >
                        {record.trackingNumber || "-"}
                    </span>
            )
        },
        {
            title: t("wms.review.scanOtherDrawer.columns.wronglyScannedCount"),
            dataIndex: "count",
            width: 180,
            align: "left",
            render: (_: number, record) => (
                record.isRouteRow
                    ? <span style={{ color: "#d48806", fontWeight: 600 }}>
                        {t("wms.review.columns.scannedOtherDriverPackages", { count: record.count || 0 })}
                    </span>
                    : <span />
            )
        },
        {
            title: t("wms.review.scanOtherDrawer.columns.assignedDriver"),
            dataIndex: "assignedDriverName",
            width: 220,
            align: "left",
            onCell: () => ({
                style: {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    verticalAlign: "top"
                }
            }),
            render: (_: string, record) => (
                <div style={{ textAlign: "left" }}>
                    <span
                        style={{
                            fontWeight: 600,
                            wordBreak: "break-word",
                            overflowWrap: "anywhere"
                        }}
                    >
                        {record.assignedDriverName || "-"}
                    </span>
                    <span
                        style={{
                            color: "#999",
                            marginLeft: 8,
                            wordBreak: "break-all"
                        }}
                    >
                        {record.assignedDriverPhone ? `+1 ${record.assignedDriverPhone}` : "-"}
                    </span>
                </div>
            )
        },
        {
            title: t("wms.review.columns.misScanMarkedType"),
            dataIndex: "isDriverNoReturnMarked",
            width: 160,
            align: "left",
            render: (_: boolean, record) => (
                record.isRouteRow
                    ? <span>-</span>
                    : <span>{record.isDriverNoReturnMarked ? t("wms.review.scanOtherDrawer.actions.driverNoReturn") : "-"}</span>
            )
        },
        {
            title: t("wms.review.scanOtherDrawer.columns.wrongScanTime"),
            dataIndex: "scanTime",
            width: 220,
            align: "center",
            render: (_: unknown, record) => (
                record.isRouteRow ? <span>-</span> : <span>{formatMisScanTime(record.scanTime)}</span>
            )
        }
    ];

    return (
        <Drawer
            title={title}
            width={1120}
            open={visible}
            onClose={onClose}
            bodyStyle={{ padding: 16 }}
        >
            <div className="flex justify-end items-center mb-4 flex-shrink-0">
                <Button
                    type="primary"
                    style={{ marginRight: 12, backgroundColor: "#8A2BE2", borderColor: "#8A2BE2" }}
                    loading={notReturnedLoading}
                    onClick={() => void handleBatchAction("notReturned")}
                >
                    {t("wms.review.scanOtherDrawer.actions.driverNoReturn")}
                </Button>
                <Button
                    type="primary"
                    style={{ backgroundColor: "#8A2BE2", borderColor: "#8A2BE2" }}
                    loading={confirmReceiveLoading}
                    onClick={openConfirmReceiveModal}
                >
                    {t("wms.review.scanOtherDrawer.actions.confirmReceive")}
                </Button>
            </div>
            <Modal
                title={t("common.reminder")}
                open={confirmReceiveModalOpen}
                onCancel={() => setConfirmReceiveModalOpen(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            type="primary"
                            loading={confirmReceiveLoading}
                            onClick={() => void executeConfirmReceive()}
                        >
                            {t("common.confirm")}
                        </Button>
                        <Button onClick={() => setConfirmReceiveModalOpen(false)}>{t("common.cancel")}</Button>
                    </div>
                }
                destroyOnClose
            >
                <p style={{ marginBottom: 0 }}>
                    {t("wms.review.scanOtherDrawer.confirmReceiveTip")}
                </p>
            </Modal>
            <Table
                dataSource={tableData}
                columns={columns}
                loading={loading}
                pagination={false}
                expandable={{
                    defaultExpandAllRows: false,
                    expandedRowKeys,
                    showExpandColumn: false,
                    onExpand: (expanded, record) => {
                        setExpandedRowKeys(prev => {
                            if (expanded) return [...prev, record.key];
                            return prev.filter(k => k !== record.key);
                        });
                    },
                    rowExpandable: (record) => record.isRouteRow && (record.children?.length || 0) > 0
                }}
                rowClassName={(record) => {
                    if (record.isRouteRow) return "";
                    return record.isDriverNoReturnMarked
                        ? "mis-scan-child-row mis-scan-no-return-row"
                        : "mis-scan-child-row";
                }}
                rowKey="key"
                tableLayout="fixed"
            />
            <style>
                {`
                    .mis-scan-no-return-row > td,
                    .mis-scan-no-return-row:hover > td {
                        background-color: #fffbe6 !important;
                    }
                `}
            </style>
            {selectedOrderId && (
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={parcelDrawerVisible}
                    onClose={() => setParcelDrawerVisible(false)}
                    orderId={selectedOrderId}
                    userRole={getUserInfo()?.account?.role?.name || ""}
                />
            )}
        </Drawer>
    );
};

export default ScanOtherDriverParcelDrawer;
